import React from 'react'
import {useState, useEffect, useContext} from 'react'
import ConfigContext from '../contexts/configContext'
import Button from '@mui/material/Button';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ErrorIcon from '@mui/icons-material/Error';
import SyncIcon from '@mui/icons-material/Sync';
import { getTimeUntil } from '../helpers/DateHelpers';

export default function PreConference({meetingId, conferenceAPIDomain, onReady, onClose, onBack}) {
  const config = useContext(ConfigContext)
  const [requestState, setRequestState] = useState('init')
  const [conferenceState, setConferenceState] = useState('notstarted')
  const [conferenceSettings, setConferenceSettings] = useState(null)
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [startingSoonTxt, setStartingSoonTxt] = useState("The conference is starting soon..")
  const [somethingWrongTxt, setSomethingWrongTxt] = useState("Sorry, something went wrong. Could not load meeting details.")
  const [loadingTxt, setLoadingTxt] = useState("Loading...")
  const [endedTxt, setEndedTxt] = useState("The conference has ended")
  const [notStartedTxt, setNotStartedTxt] = useState("The conference has not started yet..")
  const [joinEarlyButtonTxt, setJoinEarlyButtonTxt] = useState("Join Early")
  const [closeButtonTxt, setCloseButtonTxt] = useState("Close")
  const [backButtonTxt, setBackButtonTxt] = useState("Back")

  useEffect(() => {
    makeAPIRequest()
    const interval = setInterval(() => {
        // const now = new Date()
        setCurrentDateTime(new Date());
    }, 1000);
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (!config) return
    if (!Object.hasOwn(config, 'preconference')) return
    if (Object.hasOwn(config.preconference, 'startingSoonTxt')) setStartingSoonTxt(config.preconference.startingSoonTxt)
    if (Object.hasOwn(config.preconference, 'somethingWrongTxt')) setSomethingWrongTxt(config.preconference.somethingWrongTxt)
    if (Object.hasOwn(config.preconference, 'loadingTxt')) setLoadingTxt(config.preconference.loadingTxt)
    if (Object.hasOwn(config.preconference, 'endedTxt')) setEndedTxt(config.preconference.endedTxt)
    if (Object.hasOwn(config.preconference, 'notStartedTxt')) setNotStartedTxt(config.preconference.notStartedTxt)
    if (Object.hasOwn(config.preconference, 'joinEarlyButtonTxt')) setJoinEarlyButtonTxt(config.preconference.joinEarlyButtonTxt)
    if (Object.hasOwn(config.preconference, 'closeButtonTxt')) setCloseButtonTxt(config.preconference.closeButtonTxt)
    if (Object.hasOwn(config.preconference, 'backButtonTxt')) setBackButtonTxt(config.preconference.backButtonTxt)
  }, [config])

  useEffect(() => {
    if (!conferenceSettings) return

    // if a start and end date are not defined, we can just proceed
    if (!conferenceSettings.start_date && !conferenceSettings.end_date) setConferenceState('inprogress')

    const start_date = Date.parse(conferenceSettings.start_date)
    var prestart_date = new Date(start_date)
    prestart_date.setMinutes(prestart_date.getMinutes() - 5)
    const end_date = Date.parse(conferenceSettings.end_date)

    if (currentDateTime > start_date && currentDateTime < end_date) {
        setConferenceState('inprogress')
        onReady(conferenceSettings)
    } else if (currentDateTime > end_date) {
        setConferenceState('ended')
    } else if (currentDateTime > prestart_date) {
        setConferenceState('prestart')
    }
  }, [conferenceSettings, currentDateTime])

  const makeAPIRequest = () => {
    fetch(`https://${conferenceAPIDomain}/api/v4/public/conferences/${meetingId}`)
    .then(response => {
        if (response.ok) {
            response.json().then(json => {
                setConferenceSettings(
                    {
                        enable_chat: !!json.enable_chat,
                        enable_feedback: !!json.enable_feedback,
                        enable_screensharing: !!json.enable_screen_sharing,
                        enable_video: !!json.enable_video,
                        end_date: json.end_date,
                        start_date: json.start_date,
                    }
                )
                setRequestState('completed')
            })
        } else {
            setRequestState('failed')
        }
    })
    .catch(error => {
        setRequestState('failed')
    })
  }

  const renderConference = () => {
    switch(conferenceState) {
        case 'notstarted':
            return renderConferenceNotStarted()
        case 'prestart':
            return renderConferencePreStart()
        case 'ended':
            return renderConferenceEnded()
        default:
    }
  }

  const renderConferenceNotStarted = () => {
    const tr = getTimeUntil(conferenceSettings.start_date)
    return (
        <div className='flex-container-col'>
            <AccessTimeIcon sx={{fontSize: 100}}/>
            <h2>{notStartedTxt}</h2>
            <Button 
                variant="outlined"
                size="large"
                disabled
            >
                {tr.days}d {tr.hours}h {tr.minutes}m {tr.seconds}s
            </Button>
        </div>
    )
  }

  const renderConferencePreStart = () => {
    return (
        <div className='flex-container-col'>
            <AccessTimeIcon sx={{fontSize: 100}}/>
            <h2>{startingSoonTxt}</h2>
            <Button 
                variant="contained"
                size="large"
                onClick={() => onReady(conferenceSettings)}
            >
                {joinEarlyButtonTxt}
            </Button>
        </div>
    )
  }

  const renderConferenceEnded = () => {
    return (
        <div className='flex-container-col'>
            <AccessTimeIcon sx={{fontSize: 100}}/>
            <h2>{endedTxt}</h2>
            {renderFailButtons()}
        </div>
    )
  }

  const renderFailButtons = () => {
    return (
        <div className='live-call-preconference-buttons'>
            <Button 
                variant="contained"
                size="large"
                onClick={() => onBack()}
            >
                {backButtonTxt}
            </Button>
            <Button 
                variant="outlined"
                size="large"
                onClick={() => onClose()}
            >
                {closeButtonTxt}
            </Button>
        </div>
    )
  }

  const renderRequest = () => {
    switch(requestState) {
        case 'init':
            return ( 
                <>
                    <SyncIcon sx={{fontSize: 100}}/>
                    <h2>{loadingTxt}</h2>
                </>
            )
        case 'failed':
            return (
                <>
                    <ErrorIcon sx={{fontSize: 100}}/>
                    <h2>{somethingWrongTxt}</h2>
                    {renderFailButtons()}
                </>
            )
        default:
    }
  }

  return (
    <div className='live-call-preconference'>
        {requestState === 'completed' ? renderConference() : renderRequest()}
    </div>
  )
}
